import type { DecisionProof } from "../types";

/**
 * The read-back confirmation card of the "10초 검증" flow.
 *
 * Rendering rule (honesty over convenience): every ✅ line reflects the PROOF response -
 * a second, separate GET that re-read PostgreSQL - never the decision response the page
 * already holds. While the read-back is in flight the card says so; if it fails, the
 * card goes red and says that instead. No line lights up from front-end state alone.
 */

interface Props {
  proof: DecisionProof | null;
  loading: boolean;
  error: string | null;
}

export function DecisionProofCard({ proof, loading, error }: Props) {
  if (loading) {
    return (
      <div className="proof-card proof-card--pending">
        ⏳ 방금 발급된 판정번호로 서버의 저장 기록을 별도 요청으로 조회하는 중…
      </div>
    );
  }
  if (error) {
    return (
      <div className="proof-card proof-card--failed">
        <strong>🔴 저장 기록 재조회에 실패했습니다.</strong> 판정 응답은 받았지만, 별도
        조회 요청이 실패해서 저장 여부를 확인해 드릴 수 없어요. ({error}) — 이 카드는
        실제 조회가 성공했을 때만 초록불이 됩니다.
      </div>
    );
  }
  if (!proof) return null;

  const proofPath = `/v1/demo/decisions/${proof.decision_id}/proof`;
  const proofCurl = `curl -s ${window.location.origin}${proofPath}`;

  async function copyProofCurl() {
    try {
      await navigator.clipboard.writeText(proofCurl);
    } catch {
      // Clipboard denied - the command is visible in the details below.
    }
  }

  return (
    <div className="proof-card">
      <p className="proof-card-title">
        <strong>🧾 방금 그 판정, 서버 저장까지 확인했습니다</strong>
      </p>
      <ul className="proof-checklist">
        <li>✅ 서버가 송금을 판정했습니다</li>
        <li>
          ✅ 고유 판정번호가 발급됐습니다 (<code>{proof.decision_id.slice(0, 8)}…</code>)
        </li>
        <li>✅ 그 번호로 별도의 조회 요청을 보냈습니다</li>
        <li>
          {proof.ledger_record_found ? "✅" : "🔴"} PostgreSQL에서 같은 판정을 다시
          읽었습니다
        </li>
        <li>
          {proof.audit_event_found && proof.outbox_event_found ? "✅" : "🔴"} 감사 기록과
          Outbox 기록을 확인했습니다
        </li>
      </ul>
      <div className="proof-actions">
        <a
          className="proof-button"
          href={proofPath}
          target="_blank"
          rel="noreferrer"
        >
          🔍 새 창에서 원본 서버 응답 확인
        </a>
        <button type="button" className="proof-button" onClick={copyProofCurl}>
          📋 터미널 검증 명령 복사
        </button>
      </div>
      <details className="fine-print">
        <summary>🔬 기술 증거 (원문 그대로)</summary>
        <ul>
          <li>
            재조회 시각: <code>{proof.read_back_at}</code> (판정 시각{" "}
            <code>{proof.decided_at}</code>) — 새 DB 세션에서의 SELECT 결과
          </li>
          <li>
            감사 기록(audit_event): {proof.audit_event_found ? "존재" : "없음"} · Outbox
            기록(outbox_event): {proof.outbox_event_found ? "존재" : "없음"}
          </li>
          <li>
            버전: model <code>{proof.versions.model}</code> · feature{" "}
            <code>{proof.versions.feature}</code> · policy{" "}
            <code>{proof.versions.policy}</code>
          </li>
          <li>
            백엔드 <code>{proof.predictor_backend}</code> · review 임계값{" "}
            {proof.review_threshold.toFixed(4)}
            {proof.step_up_threshold !== null &&
              ` · step-up 임계값 ${proof.step_up_threshold.toFixed(4)}`}
          </li>
          <li>
            배포 커밋:{" "}
            {proof.build_commit_sha ? (
              <code>{proof.build_commit_sha.slice(0, 12)}</code>
            ) : (
              "이 환경에서는 제공되지 않음 (Render 배포에서만 RENDER_GIT_COMMIT 노출)"
            )}
          </li>
          <li>
            피처 스냅샷: <code>{JSON.stringify(proof.feature_snapshot)}</code>
          </li>
          <li>
            터미널 검증: <code>{proofCurl}</code>
          </li>
        </ul>
      </details>
    </div>
  );
}
